import { Fragment } from 'react';
import Box from '@mui/material/Box';
import { Pagination, useTranslate } from 'react-admin';
import { ListBase, WithListContext } from 'react-admin';
import { AVATAR_TRACEABILITY_NOTES } from '@/utils/CONST';
import NoteResolver from '@/components/common/notes/note-resolver/note-resolver';
import CollapseMenu from '@/components/common/collapse-menu/collapse-menu';
import extractIdFromURI from '@/utils/id';
import { CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import HistoryIcon from '@mui/icons-material/History';
import { map } from 'lodash';
import useGetAvatar from '@/hooks/useGetAvatar';

interface Props {
    selectedAvatarUriId: string;
    /** Repasa a CollapseMenu: scroll delegado al padre (p. ej. diálogo agenda). */
    collapseMenuDisableInternalScroll?: boolean;
    collapseMenuDialogSection?: boolean;
}

const AvatarNotes = ({
    selectedAvatarUriId,
    collapseMenuDisableInternalScroll = false,
    collapseMenuDialogSection = false,
}: Props) => {
    const translate = useTranslate();

    const { avatar } = useGetAvatar({
        avatarUriId: selectedAvatarUriId,
    });

    return (
        <CollapseMenu
            icon={<HistoryIcon />}
            text={translate('notes.list.label')}
            disableInternalScroll={collapseMenuDisableInternalScroll}
            dialogSection={collapseMenuDialogSection}
        >
            <Box
                sx={
                    collapseMenuDialogSection
                        ? {
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                              p: 0,
                              m: 0,
                          }
                        : {
                              p: 1,
                              pb: 0,
                          }
                }
            >
                <ListBase
                    resource={AVATAR_TRACEABILITY_NOTES}
                    filter={{
                        avatarId: extractIdFromURI(selectedAvatarUriId),
                    }}
                    disableSyncWithLocation
                    queryOptions={{
                        enabled: selectedAvatarUriId !== undefined,
                    }}
                    sort={{
                        field: 'createdAt',
                        order: 'DESC',
                    }}
                >
                    <WithListContext
                        render={({ data, isLoading }) => (
                            <Fragment>
                                {isLoading ? (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            justifyContent: 'center',
                                            ...(collapseMenuDialogSection
                                                ? { py: 2, px: 0 }
                                                : { p: 1, pb: 2 }),
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Fragment>
                                        {data && data.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: collapseMenuDialogSection ? 2 : 1,
                                                    backgroundColor: '#F1F1F1',
                                                    p: collapseMenuDialogSection ? 2 : 1,
                                                    borderRadius: 2,
                                                }}
                                            >
                                                {map(data, (note: any) => (
                                                    <NoteResolver
                                                        type={note.classMapper}
                                                        note={note}
                                                        avatarName={avatar?.name}
                                                        key={note.id}
                                                    />
                                                ))}
                                            </Box>
                                        )}

                                        {data && data.length > 0 && (
                                            <Pagination
                                                sx={{
                                                    backgroundColor: 'white',
                                                }}
                                            />
                                        )}

                                        {data && data.length === 0 && (
                                            <Typography
                                                sx={{
                                                    m: 0,
                                                    pb: collapseMenuDialogSection ? 0 : 1,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {translate('messages.no_items')}
                                            </Typography>
                                        )}
                                    </Fragment>
                                )}
                            </Fragment>
                        )}
                    />
                </ListBase>
            </Box>
        </CollapseMenu>
    );
};

export default AvatarNotes;
